import { Box, Sheet, Typography, IconButton, AspectRatio, Stack } from '@mui/joy';
import { Close } from '@mui/icons-material';
import EventIcon from '@mui/icons-material/Event';
import { Link, useParams } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { RecipeImage } from '@/features/recipes/components/RecipeImage';
import { RecipeDetails } from '@/features/meals/meal-plan/components/RecipeDetails';

// Renders a meal slot of any kind -- recipe, standalone ingredient, or event.
// Which kind it is stays an implementation detail here: callers pass the
// resolved item (see resolveMealPlanEntryItem) and let this decide what a slot
// of that kind looks like, including where (and whether) it links.
// variant picks the surface this entry is drawn on: 'default' is a white card
// for the mobile list (which sits directly on the page's gray background),
// 'calendar' is a tinted card for the desktop calendar's white day columns.
export function MealEntry({ kind = 'recipe', item, entry, nutrition, isSelected, onDelete, variant = 'default' }) {
  const { householdId } = useParams();
  const theme = useTheme();
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isWideScreen = useMediaQuery(theme.breakpoints.up('xl'));
  const isCalendar = variant === 'calendar';

  const titleLevel = isCalendar ? 'title-mdsm' : 'title-md';
  const iconSize = isCalendar ? (isNarrowScreen ? 28 : isWideScreen ? 40 : 34) : 44;
  const gap = isNarrowScreen ? 0.75 : 1;

  return (
    <Sheet
      variant="soft"
      sx={{
        p: isCalendar ? 0.5 : 1,
        borderRadius: isCalendar ? 'sm' : 'md',
        display: 'flex',
        alignItems: 'center',
        gap,
        position: 'relative',
        backgroundColor: isCalendar ? 'var(--joy-palette-neutral-100)' : '#fff',
        boxShadow: isSelected ? 'var(--joy-shadow-lg)' : 'var(--joy-shadow-md)',
      }}
    >
      {kind === 'recipe' && (
        <RecipeImage
          recipe={item}
          orientation="horizontal"
          size={iconSize}
          sx={{ borderRadius: 'sm', flexShrink: 0, pointerEvents: 'none', alignSelf: 'flex-start' }}
        />
      )}

      {kind === 'event' && (
        <AspectRatio ratio="1" sx={{ width: iconSize, borderRadius: 'sm', flexShrink: 0 }}>
          {item.image?.url ? (
            <img src={item.image.url} srcSet={item.image.srcset} sizes={`${iconSize}px`} alt={item.name} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.tertiary' }}>
              <EventIcon />
            </Box>
          )}
        </AspectRatio>
      )}

      <Stack direction="column" gap={isCalendar ? 0.25 : 0.5} sx={{ flex: 1, minWidth: 0 }}>
        {kind === 'recipe' ? (
          <>
            <Link to={`/h/${householdId}/recipes/${item.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography
                level={titleLevel}
                sx={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', mr: 3 }}
              >
                {item.name}
              </Typography>
            </Link>
            {nutrition && <RecipeDetails recipe={item} nutrition={nutrition} />}
          </>
        ) : (
          <>
            <Typography
              level={titleLevel}
              sx={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', mr: 3 }}
            >
              {item.name}
            </Typography>
            {kind === 'ingredient' && (
              <Typography level="title-xs">
                {entry.ingredient?.amount} {entry.ingredient?.unit}
              </Typography>
            )}
          </>
        )}
      </Stack>

      {onDelete && (
        <IconButton
          size="sm"
          variant="plain"          
          onClick={onDelete}
          sx={{ flexShrink: 0, position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: isCalendar ? -2 : 6, '--Icon-fontSize': isCalendar ? '14px' : '16px' }}
        >
          <Close />
        </IconButton>
      )}
    </Sheet>
  );
}
